import { override } from "@agrigate/ts-utils/lib/function";
import type { Dict, MaybeN } from "@agrigate/ts-utils/lib/types";
import { memoize } from "lodash";
import { fn } from "overridable-fn";
import type { PostHog } from "posthog-js";
import { v4 as uuidv4 } from "uuid";

export const trackingIdKey = "ag:trackingId:v1";

export const getPostHog = fn.memo((): PostHog => override());
export const getPostHogPrefix = fn.memo((): string => override());

// biome-ignore lint/suspicious/noExplicitAny: Allow adhoc properties to be passed
export type EventProps = Record<string, any>;

const defaultProps: Dict<string> = {};

export const setDefaultProp = (key: string, value: string) => {
    defaultProps[key] = value;
};

export const trackEvent = (
    subject: string,
    action: string,
    props?: EventProps,
): void => {
    console.log("POSTHOG trackEvent fn: ", subject, action, props);
    try {
        const eventName = `${getPostHogPrefix()}:${subject}:${action}`;
        const finalProps = props ? { ...defaultProps, ...props } : defaultProps;
        getPostHog()?.capture(eventName, finalProps);
        console.log("[PostHog] Tracked event:", eventName, props);
    } catch (error) {
        console.error("[PostHog] Failed to track event:", error);
    }
};

export const trackView = (screenName: string, props?: EventProps): void => {
    console.log("POSTHOG trackView fn: ", screenName, props);
    getPostHog()?.capture("$pageview", {
        $current_url: window.location.href,
        screenName: `${getPostHogPrefix()}:${screenName}`,
        ...defaultProps,
        ...props,
    });
};

export const identifyUser = (trackingId: string) => {
    try {
        getPostHog()?.identify(trackingId);
        console.log("[PostHog] User identified:", trackingId);
    } catch (error) {
        console.error("[PostHog] Failed to identify user:", error);
    }
};

export const resetUser = (): void => {
    console.log("reset user called");
    getPostHog()?.reset();
};

const newTrackingId = memoize(() => `${uuidv4()}`);

export const getTrackingId = (updatePosthog?: boolean) => {
    let trackingId: MaybeN<string>;
    try {
        trackingId = localStorage.getItem(trackingIdKey);
    } catch (e) {
        console.error("Failed to get tracking id", e);
    }

    if (!trackingId) {
        trackingId = newTrackingId();
        try {
            localStorage.setItem(trackingIdKey, trackingId);
        } catch (e) {
            console.error("Failed to set tracking id", e);
        }
    }

    if (updatePosthog) identifyUser(trackingId);

    return trackingId;
};

export const setTrackingId = (trackingId: MaybeN<string>) => {
    if (!trackingId) return;
    try {
        localStorage.setItem(trackingIdKey, trackingId);
        identifyUser(trackingId);
    } catch (e) {
        console.error("Failed to set tracking id", e);
    }
};
